import { ref } from 'vue'
import { apiFetch } from '../services/api'
import { GOOGLE_INTEGRATION_URL } from '../env'

export interface GoogleWorkspaceStatus {
  provider: string
  connected: boolean
  email: string | null
  connected_at: string | null
}

export interface GoogleWorkspaceTestResponse {
  email: string
  messages_total: number
  messages_unread: number
  inbox_messages_total: number
  inbox_messages_unread: number
  can_send: boolean
}

export function useGoogleWorkspaceIntegration() {
  const status = ref<GoogleWorkspaceStatus | null>(null)
  const loading = ref(false)
  const connecting = ref(false)
  const disconnecting = ref(false)
  const saving = ref(false)
  const testing = ref(false)
  const error = ref('')
  const testResult = ref<GoogleWorkspaceTestResponse | null>(null)
  const isLocalMode = !GOOGLE_INTEGRATION_URL

  async function load() {
    loading.value = true
    error.value = ''
    try {
      const resp = await apiFetch('/user/integrations/google-workspace')
      status.value = resp as GoogleWorkspaceStatus
    } catch (e: any) {
      error.value = e?.message || 'Failed to load Google Workspace status'
    } finally {
      loading.value = false
    }
  }

  async function connect() {
    // Proxy mode: redirect to OAuth flow
    connecting.value = true
    error.value = ''
    const current = window.location.origin + '/integrations?connection=google_workspace'
    window.location.href = `${GOOGLE_INTEGRATION_URL}/v2/google/workspace/authorize?redirect_uri=${current}`
    // Note: connecting stays true because the page will redirect away
  }

  async function save(accessToken: string, refreshToken: string) {
    saving.value = true
    error.value = ''
    try {
      await apiFetch('/user/integrations/google-workspace/credentials', {
        method: 'PUT',
        body: { access_token: accessToken, refresh_token: refreshToken },
      })
      await load()
    } catch (e: any) {
      error.value = e?.message || 'Failed to save credentials'
    } finally {
      saving.value = false
    }
  }

  async function disconnect() {
    disconnecting.value = true
    error.value = ''
    try {
      if (GOOGLE_INTEGRATION_URL) {
        try {
          await apiFetch(`${GOOGLE_INTEGRATION_URL}/v2/google/workspace/token`, { method: 'DELETE', responseType: 'raw' })
        } catch (e: any) {
          // 404 says we have already disconnected OAuth; we are okay with that
          // so delete can be idempotent and we can heal desynched status from
          // an earlier client side error
          if (e?.status !== 404) throw e
        }
      }

      // Update UserIntegration row
      await apiFetch('/user/integrations/google-workspace', { method: 'DELETE' })
      status.value = { provider: 'google_workspace', connected: false, email: null, connected_at: null }
    } catch (e: any) {
      error.value = e?.message || 'Failed to disconnect Google Workspace'
    } finally {
      disconnecting.value = false
    }
  }

  async function test(accessToken?: string, refreshToken?: string) {
    testing.value = true
    error.value = ''
    testResult.value = null
    const body: Record<string, string> = {}
    if (accessToken?.trim()) body.access_token = accessToken.trim()
    if (refreshToken?.trim()) body.refresh_token = refreshToken.trim()
    try {
      const resp = await apiFetch('/user/integrations/google-workspace/test', {
        method: 'POST',
        body,
      })
      testResult.value = resp as GoogleWorkspaceTestResponse
    } catch (e: any) {
      error.value = e?.message || 'Connection test failed'
    } finally {
      testing.value = false
    }
  }

  async function handleOAuthCallback(onConnected?: () => void) {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('connection') !== 'google_workspace')
      return

    const maybeError = urlParams.get('error')
    window.history.replaceState({}, '', window.location.pathname)
    if (maybeError) {
      error.value = `Google Workspace connection failed. Please try again. Detail: ${maybeError}`
      return
    }

    connecting.value = true
    try {
      await apiFetch('/user/integrations/google-workspace/notify-connected', { method: 'POST' })
    } catch (e) {
      console.warn('[gmail] Failed to set up polling history ID after OAuth connect:', e)
    } finally {
      connecting.value = false
    }
    onConnected?.()
  }

  return {
    status,
    loading,
    connecting,
    disconnecting,
    saving,
    testing,
    error,
    testResult,
    isLocalMode,
    load,
    connect,
    save,
    disconnect,
    test,
    handleOAuthCallback,
  }
}
